import {
  Box,
  Card,
  Center,
  Collapse,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react"
import type { FC } from "react"
import { useEffect, useRef, useState } from "react"
import { isMobile } from "react-device-detect"

import SmallDelegateUserIdentity from "@/common/components/columns/SmallDelegateUserIdentity"
import LoadMoreButton from "@/common/components/LoadMoreButton"
import MarkdownViewer from "@/common/components/MarkdownViewer"
import { getWeightLabel, stringToBigNumber } from "@/common/helpers/bignumber"
import DelegateButton from "@/delegation/components/DelegateButton"
import type { Account, Delegate, Organization } from "@/query/graphql"
import { useDelegateStatementQuery } from "@/query/graphql"

type AccountCardDetailedProps = {
  delegate: Pick<Delegate, "votesCount"> & {
    account: Pick<Account, "bio" | "name" | "picture" | "address" | "twitter">
  }
  tokenSymbol?: string
  organization: Organization
  decimals?: number
  isWhiteLabel: boolean
  detailed: boolean
}
const AccountCardBio: FC<AccountCardDetailedProps> = ({
  delegate,
  tokenSymbol,
  organization,
  decimals,
  isWhiteLabel,
}) => {
  const [startingHeight, setStartingHeight] = useState(isMobile ? 50 : 150)
  const [show, setShow] = useState(false)

  const { data, isLoading } = useDelegateStatementQuery(
    {
      input: {
        address: delegate.account.address,
        organizationId: organization.id,
      },
    },
    { enabled: Boolean(delegate.account.address) && Boolean(organization.id) },
  )

  const statement = data?.delegate?.statement?.statement ?? delegate.account.bio

  const markdownRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint: biome-migration
  useEffect(() => {
    if (
      markdownRef &&
      markdownRef.current &&
      markdownRef.current.clientHeight > 0
    ) {
      if (markdownRef.current.clientHeight < startingHeight) {
        setStartingHeight(markdownRef.current.clientHeight)
      }
    }
  }, [statement, startingHeight])

  const showReadMore = Boolean(statement) && startingHeight > 50

  const handleToggleLoadMore = () => {
    setShow(!show)
  }

  const voteWeight = delegate.votesCount
  const bigVoteWeight = stringToBigNumber(voteWeight)
  const voteWeightFormatted = decimals
    ? getWeightLabel(bigVoteWeight, decimals)
    : undefined

  if (!statement) return null

  const canDelegate = organization.tokenIds?.length > 0

  return (
    <Card
      background="white"
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow="xs"
    >
      <Stack
        direction="column"
        flex={1}
        justifyContent="space-between"
        padding={5}
        spacing={4}
      >
        <HStack alignItems="flex-start" justifyContent="space-between">
          <SmallDelegateUserIdentity
            account={delegate.account}
            isWhiteLabel={isWhiteLabel}
            organization={organization}
            tokenSymbol={tokenSymbol}
            votes={voteWeightFormatted}
          />
          {canDelegate ? (
            <DelegateButton
              account={delegate.account}
              delegateAddress={delegate.account.address}
              fontSize="xs"
              organization={organization}
              px={5}
              size="sm"
              variant="secondary"
            >
              Delegate
            </DelegateButton>
          ) : null}
        </HStack>
        <Text
          color={!statement ? "gray.400" : "gray.700"}
          fontSize="sm"
          mb={3}
          minHeight="50px"
        >
          {!isLoading ? (
            statement ? (
              <Collapse in={show} startingHeight={startingHeight}>
                <Box ref={markdownRef}>
                  <MarkdownViewer content={statement} />
                </Box>
              </Collapse>
            ) : (
              "No statement provided"
            )
          ) : (
            "Loading statement..."
          )}
        </Text>
      </Stack>
      {showReadMore ? (
        <Stack
          backgroundColor="white"
          borderBottomRadius="lg"
          borderTopColor="gray.200"
          borderTopWidth="1px"
        >
          <Center flex={1}>
            <LoadMoreButton
              flex={1}
              isDisabled={!showReadMore}
              label={show ? "Show less" : "Read more"}
              variant="tertiary"
              onClick={handleToggleLoadMore}
            />
          </Center>
        </Stack>
      ) : null}
    </Card>
  )
}

export default AccountCardBio
